import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import StreamingLinks from './ui/StreamingLinks';
import MatrixRain from './ui/MatrixRain';
import LazyIframe from './ui/LazyIframe';

const releases = [
  {
    title: 'Holiday Flex',
    type: 'Album',
    year: '2025',
    image: '/images/holidayflex.png',
    spotifyUrl: 'https://open.spotify.com/album/holiday-flex',
  },
  {
    title: 'I Survived My Own Mind',
    type: 'Album',
    year: '2025',
    image: '/images/stronk.png',
    spotifyUrl: 'https://open.spotify.com/album/i-survived-my-own-mind',
  },
  {
    title: 'Rabbit Mode',
    type: 'Album',
    year: '2025',
    image: '/images/coverart.png',
    spotifyUrl: 'https://open.spotify.com/album/rabbit-mode',
  },
  {
    title: 'Ana Baby',
    type: 'Single',
    year: '2025',
    image: '/images/anababycover.png',
    spotifyUrl: 'https://open.spotify.com/album/ana-baby',
  },
];

export default function Music() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="music" className="relative py-24 md:py-32 bg-cyber-darker overflow-hidden">
      {/* Matrix Rain Background */}
      <MatrixRain opacity={0.08} />
      
      {/* Background Elements */}
      <div className="absolute inset-0 cyber-grid opacity-20" />
      <div 
        className="absolute top-0 left-1/4 w-96 h-96 rounded-full opacity-10"
        style={{
          background: 'radial-gradient(circle, rgba(0, 212, 255, 0.5) 0%, transparent 70%)',
        }}
      />
      <div 
        className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full opacity-10"
        style={{
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.5) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4" ref={ref}>
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-orbitron text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            <span className="text-white">THE </span>
            <span className="bg-gradient-to-r from-cyber-cyan to-cyber-purple bg-clip-text text-transparent">
              MUSIC
            </span>
          </h2>
          <p className="text-white/60 font-rajdhani text-lg max-w-2xl mx-auto">
            Down the rabbit hole you go. Trap, hip-hop, pop, country, rock—AI-powered bangers that hit different.
          </p>
        </motion.div>

        {/* Spotify Embed + Lo-Fi CTA */}
        <div className="mb-16 flex flex-col lg:flex-row gap-6 items-stretch max-w-6xl mx-auto">
          <motion.div
            className="flex-1 lg:max-w-4xl"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="glass-card p-4 rounded-2xl h-full">
              <LazyIframe
                src="https://open.spotify.com/embed/playlist/5BvvxFrjN9tcklCk9hKM6v?utm_source=generator&theme=0"
                title="White Rabbit Bangers Spotify Playlist"
                height={500}
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                className="opacity-90 hover:opacity-100 transition-opacity"
              />
            </div>
          </motion.div>

          <motion.div
            className="lg:w-64 flex-shrink-0"
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="glass-card p-6 rounded-2xl h-full flex flex-col items-center justify-center text-center">
              <div className="text-4xl mb-4">🎧</div>
              <h3 className="font-orbitron text-xl font-bold text-white mb-2">
                Looking for Lo-Fi?
              </h3>
              <p className="text-white/60 font-rajdhani text-sm mb-6">
                Chill beats to relax, study, or vibe to.
              </p>
              <a
                href="#lofi"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyber-cyan to-cyber-purple text-cyber-dark font-orbitron font-semibold rounded-full hover:shadow-lg hover:shadow-cyber-cyan/30 transition-all duration-300 hover:scale-105"
              >
                <span>Check It Out</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </a>
            </div>
          </motion.div>
        </div>

        {/* Releases Grid */}
        <motion.div
          className="mb-16"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h3 className="font-orbitron text-2xl font-bold text-center mb-8 text-white/90">
            Latest Releases
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {releases.map((release, index) => (
              <motion.a
                key={release.title}
                href={release.spotifyUrl}
                className="group relative"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.5 + index * 0.1 }}
                whileHover={{ scale: 1.03 }}
              >
                <div className="glass-card overflow-hidden rounded-xl">
                  <div className="aspect-square bg-cyber-dark relative overflow-hidden">
                    <img
                      src={release.image}
                      alt={release.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-cyber-cyan/0 group-hover:bg-cyber-cyan/20 transition-colors duration-300 flex items-center justify-center">
                      <motion.div
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                        whileHover={{ scale: 1.1 }}
                      >
                        <div className="w-12 h-12 rounded-full bg-cyber-cyan flex items-center justify-center shadow-lg shadow-cyber-cyan/50">
                          <svg className="w-5 h-5 text-cyber-dark ml-1" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                  
                  {/* Release Info */}
                  <div className="p-3">
                    <h4 className="font-orbitron font-semibold text-sm text-white truncate group-hover:text-cyber-cyan transition-colors">
                      {release.title}
                    </h4>
                    <p className="text-white/50 text-xs font-rajdhani">
                      {release.type} • {release.year}
                    </p>
                  </div>
                </div>
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* All Platforms */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <p className="text-white/50 text-sm font-rajdhani mb-6 uppercase tracking-widest">
            Stream everywhere
          </p>
          <StreamingLinks variant="horizontal" size="lg" />
        </motion.div>
      </div>

      {/* Waveform Decoration */}
      <div className="absolute bottom-0 left-0 right-0 h-24 overflow-hidden opacity-20">
        <svg viewBox="0 0 1200 120" className="w-full h-full" preserveAspectRatio="none">
          <motion.path
            d="M0,60 Q150,20 300,60 T600,60 T900,60 T1200,60 L1200,120 L0,120 Z"
            fill="url(#waveGradient)"
            initial={{ d: "M0,60 Q150,20 300,60 T600,60 T900,60 T1200,60 L1200,120 L0,120 Z" }}
            animate={{
              d: [
                "M0,60 Q150,20 300,60 T600,60 T900,60 T1200,60 L1200,120 L0,120 Z",
                "M0,60 Q150,100 300,60 T600,60 T900,60 T1200,60 L1200,120 L0,120 Z",
                "M0,60 Q150,20 300,60 T600,60 T900,60 T1200,60 L1200,120 L0,120 Z",
              ],
            }}
            transition={{ duration: 4, repeat: Infinity }}
          />
          <defs>
            <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#00d4ff" />
              <stop offset="50%" stopColor="#8b5cf6" />
              <stop offset="100%" stopColor="#ec4899" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </section>
  );
}

